import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import router from '@/router';
import SecureLS from 'secure-ls';
import {
  IAutocreditAccountBody,
  IAutocreditAuthorizeBody,
  IAutocreditMarkPostedBody,
  IAutocreditReprocessBody,
} from '@/types/types';

let ls = new SecureLS({
  isCompression: false,
});

let AutocreditRequest = () => {
  let baseHeader: AxiosInstance = Axios.create({
    baseURL: process.env.VUE_APP_AUTOCREDIT_BASE_URL,
    headers: {
      'x-channel-id': 'admin_web',
    },
    timeout: 80000,
  });

  baseHeader.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      config.headers.common['Accept'] = 'application/json';
      config.headers.common['Content-Type'] = 'application/json';
      // Guarded: JSON.parse throws on the empty string SecureLS returns for a
      // missing key, which would fail the request outright when logged out.
      let session: string | undefined;
      try {
        session = JSON.parse(ls.get('cloud.access'))?.auth?.user?.token;
        // eslint-disable-next-line no-empty
      } catch (e) {}
      if (session) {
        config.headers.common['authorization'] = `Bearer ${session}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    },
  );

  baseHeader.interceptors.response.use(
    (response: AxiosResponse) => {
      return Promise.resolve(response);
    },
    error => {
      if (error.response) {
        if (error.response.status === 401) {
          localStorage.clear();
          router.replace('/signin').then();
        }
      }
      return Promise.reject(error);
    },
  );
  return baseHeader;
};

export class AutocreditService {
  static fetchReviewPending(query?: string) {
    return AutocreditRequest().get(`/autocredit/review-pending${query || ''}`);
  }

  static authorizeReviewPending(adminId: string, body: IAutocreditAuthorizeBody) {
    return AutocreditRequest().post(`/autocredit/review-pending/authorize`, body, {
      headers: { 'x-admin-id': adminId },
    });
  }

  static fetchLedgers(query?: string) {
    return AutocreditRequest().get(`/autocredit/ledgers${query || ''}`);
  }

  static reprocessLedger(adminId: string, ledgerId: string, body: IAutocreditReprocessBody) {
    return AutocreditRequest().post(`/autocredit/ledgers/${ledgerId}/reprocess`, body, {
      headers: { 'x-admin-id': adminId },
    });
  }

  static markLedgerPosted(adminId: string, ledgerId: string, body: IAutocreditMarkPostedBody) {
    return AutocreditRequest().post(`/autocredit/ledgers/${ledgerId}/mark-posted`, body, {
      headers: { 'x-admin-id': adminId },
    });
  }

  static fetchAccounts(query?: string) {
    return AutocreditRequest().get(`/autocredit/accounts${query || ''}`);
  }

  static upsertAccount(body: IAutocreditAccountBody) {
    return AutocreditRequest().post(`/autocredit/accounts`, body);
  }
}
